import {Regions} from "./../model/Region"
import {UniversityBranches} from "./../model/UniversityBranch"
import {Specialities} from "./../model/Speciality"
import {Demand, DemandList} from "../model/Demand"
import {Divisions} from "../model/Division"

import {SheetData} from "./../Excel/export"
import {RowData, getRegion, getCompany} from "./../model/utils/utils"
import {User} from "../model/User";
import {
    border,
    Columns,
    demandColumns,
    demandColumnToYear,
    employeeColumns,
    makeEmployeeData,
    makeEmptyRow,
    makeSheet,
    makeSpecialityData,
    makeUniversityData,
    Rows,
    specialityColumns,
    specialityMerges,
    universityColumns,
    universityMerges,
    vacancyColumns,
} from "./common";

const columnsOrder = [
    ...specialityColumns,
    Columns.isOpk,
    ...employeeColumns,
    ...vacancyColumns,
    ...demandColumns,
    Columns.entranceFactorDemand,
    Columns.entranceFactorTarget,
    Columns.businessType,
    Columns.employmentRegion,
    Columns.country,
    Columns.notes,
    ...universityColumns,
]

type Data = {
    demand: DemandList
    divisions: Divisions
    regions: Regions
    specialities: Specialities
    universityBranches: UniversityBranches
    monitoring2019Data: {
        nuclear: {[year: string]: number}
        total: {[year: string]: number}
    }
    meta: {
        company: string
        user: User
    }
}

const composeData = ({
    demand,
    divisions,
    regions,
    specialities,
    universityBranches,
}: Data): RowData[] => {
    let rawResult: RowData[] = []

    demand.map(({
        id,
        companyId,
        courseOfStudyName,
        entranceFactorDemand,
        entranceFactorTarget,
        specialityId,
        regionId,
        universityBranchId,
        employees,
        demandTotal,
        demandByYear,
        nationalProjectName,
        englishLevelName,
        languageName,
        businessTypeName,
        employmentRegionId,
        countryName,
        notes,
    }: Demand) => {
        if (!demandTotal) {
            return
        }

        const {
            isOpk,
        } = getCompany(divisions, companyId)

        const {
            name: employmentRegionName,
        } = getRegion(regions, employmentRegionId)
    
        let row = makeEmptyRow(columnsOrder, {
            ...makeSpecialityData({
                id,
                courseOfStudyName,
                divisions,
                companyId,
                specialities,
                specialityId,
                nationalProjectName,
                englishLevelName,
                languageName,
            }),
            ...makeUniversityData({
                regions,
                regionId,
                universityBranches,
                universityBranchId,
            }),
            ...makeEmployeeData({employees}),
            [Columns.entranceFactorDemand]: entranceFactorDemand,
            [Columns.entranceFactorTarget]: entranceFactorTarget,
            [Columns.businessType]: businessTypeName,
            [Columns.isOpk]: (isOpk/* && specialitiesOpk.has(specialityId)*/) ? "Да" : "Нет",
            [Columns.employmentRegion]: employmentRegionName,
            [Columns.country]: countryName,
            [Columns.notes]: notes,
            ...demandColumns .reduce((row, column) => ({...row, [column]: demandByYear[demandColumnToYear[column]] || ""}), {}),
            ...vacancyColumns.reduce((row, column) => ({...row, [column]: demandByYear[demandColumnToYear[column]] || ""}), {}),
        })

        rawResult = rawResult.concat([row])
    })

    return rawResult
}

export const demandWs = (data: Data): SheetData => makeSheet({
    sheetTitle: "Общие потребности",
    title1: "Данные о трудоустройстве и потребностях",
    title2: "организаций Госкорпорации «Росатом» в выпускниках образовательных организаций.",
    company: data.meta.company,
    fullName: data.meta.user.executor.fullName,
    columnsOrder,
    heading1Row: {
        [employeeColumns[0]]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "top"}},    v: "Общие вакансии"},
        [Columns.businessType]:            {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Направление бизнеса"},
        [Columns.employmentRegion]:       {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Регион трудоустройства"},
        [Columns.country]:                {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Гражданство"},
        [Columns.notes]:                  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "center"}}, v: "Комментарий"},
    },
    heading2Row: {
        [employeeColumns[0]]: {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "top"}},    v: "Факт"},
        [vacancyColumns[0]]:  {s: {border, font: {bold: true}, alignment: {wrapText: true, horizontal: "center", vertical: "top"}},    v: "Прогноз"},
    },
    merges: [
        ...specialityMerges,

        [employeeColumns[0], Rows.Heading1, demandColumns[demandColumns.length - 1],    Rows.Heading1],
        [employeeColumns[0], Rows.Heading2, employeeColumns[1], Rows.Heading2],
        [vacancyColumns[0], Rows.Heading2, demandColumns[demandColumns.length - 1], Rows.Heading2],

        [Columns.entranceFactorDemand, Rows.Heading1, Columns.entranceFactorDemand, Rows.Heading3],
        [Columns.entranceFactorTarget, Rows.Heading1, Columns.entranceFactorTarget, Rows.Heading3],
        [Columns.isOpk,                Rows.Heading1, Columns.isOpk,                Rows.Heading3],
        [Columns.businessType,         Rows.Heading1, Columns.businessType,         Rows.Heading3],
        [Columns.employmentRegion,     Rows.Heading1, Columns.employmentRegion,     Rows.Heading3],
        [Columns.country,              Rows.Heading1, Columns.country,              Rows.Heading3],
        [Columns.notes,                Rows.Heading1, Columns.notes,                Rows.Heading3],

        ...universityMerges,
    ],
    data: composeData(data),
    specialities: data.specialities,
    monitoring2019Data: data.monitoring2019Data,
    addNuclearFooter: true,
    addNationalProjectFooter: true,
})
